// components/skeletons/BeaconTreeSkeleton.tsx
// Placeholder for the beacon branches inside the dark "Beacon Hub" panel while
// the first load runs: a trunk with a couple of short branches, each ending in
// a compact one-row card (sheet tile + name, action buttons). Light-on-dark, to
// sit inside the teal hub. Self-contained: its own tree styles, no dependency
// on the hub's CSS.

import React from 'react';
import { Bone, SkeletonStyles } from './Bone';

interface BeaconTreeSkeletonProps {
  /** number of placeholder branches */
  count?: number;
}

const CSS = `
.bsk-leaf { position: relative; padding: 6px 0 6px 54px; }
.bsk-trunk { position: absolute; left: 22px; top: 0; bottom: 0; width: 2px; background: rgba(255,255,255,0.14); }
.bsk-leaf.bsk-last .bsk-trunk { bottom: 50%; }
.bsk-branch { position: absolute; left: 22px; top: 50%; width: 32px; height: 2px; margin-top: -1px; background: rgba(255,255,255,0.14); }
.bsk-knot { position: absolute; left: 18px; top: 50%; width: 10px; height: 10px; margin-top: -5px; border-radius: 50%; border: 2px solid #0f4a46; background: rgba(255,255,255,0.3); }
`;

const BeaconTreeSkeleton: React.FC<BeaconTreeSkeletonProps> = ({ count = 2 }) => (
  <div role="status" aria-busy="true" aria-label="Loading beacons">
    <SkeletonStyles />
    <style>{CSS}</style>
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className={`bsk-leaf${i === count - 1 ? ' bsk-last' : ''}`}>
        <span className="bsk-trunk" />
        <span className="bsk-branch" />
        <span className="bsk-knot" />

        <div
          style={{
            borderRadius: '12px',
            padding: '9px 12px',
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.12)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            flexWrap: 'wrap',
          }}
        >
          {/* sheet tile + name / meta */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: '1 1 200px', minWidth: 0 }}>
            <Bone dark w={30} h={30} r={8} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: 1 }}>
              <Bone dark w={`${45 + ((i * 13) % 25)}%`} h={10} />
              <Bone dark w="30%" h={8} />
            </div>
          </div>
          {/* actions */}
          <div style={{ display: 'flex', gap: '6px' }}>
            <Bone dark w={24} h={24} r={7} />
            <Bone dark w={24} h={24} r={7} />
            <Bone dark w={24} h={24} r={7} />
          </div>
        </div>
      </div>
    ))}
  </div>
);

export default BeaconTreeSkeleton;